/**
 * NavigatorX Notification Analytics Dashboard
 *
 * Push notification performance analytics:
 * - Total notifications sent by type
 * - Read/open rates
 * - FCM token health (active tokens)
 * - User notification preference adoption
 * - Notification delivery breakdown
 * - Recent notification activity feed
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Bell,
  BellOff,
  Mail,
  Eye,
  EyeOff,
  Smartphone,
  RefreshCw,
  Loader2,
  BarChart3,
  Users,
  CheckCircle,
  AlertTriangle,
  PieChart,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  collectionGroup
} from 'firebase/firestore';
import { navDb } from '../../../utils/firebase/client';
import { NAV_USERS_COLLECTION, timestampToDate } from '../../../utils/services/navigatorXService';
import type { NavUser } from '../../../types/navigatorx';

interface NotificationAnalyticsProps {
  embedded?: boolean;
}

interface NotificationRecord {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: Date | null;
  sentBy?: string;
}

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  'referral_signup': 'Referral Signup',
  'referral_month1': 'Referral Month 1',
  'referral_credit': 'Referral Credit',
  'referral_churned': 'Referral Churned',
  'trial_expiring': 'Trial Expiring',
  'trial_expired': 'Trial Expired',
  'payment_failed': 'Payment Failed',
  'subscription_renewed': 'Subscription Renewed',
  'hazard_nearby': 'Hazard Nearby',
  'new_feature': 'New Feature',
  'voice_ready': 'Voice Ready',
  'system': 'System',
  'broadcast': 'Broadcast',
  'admin': 'Admin Message'
};

const NOTIFICATION_TYPE_COLORS: Record<string, string> = {
  'referral_signup': 'bg-pink-100 text-pink-700',
  'referral_month1': 'bg-pink-100 text-pink-700',
  'referral_credit': 'bg-green-100 text-green-700',
  'referral_churned': 'bg-red-100 text-red-700',
  'trial_expiring': 'bg-yellow-100 text-yellow-700',
  'trial_expired': 'bg-red-100 text-red-700',
  'payment_failed': 'bg-red-100 text-red-700',
  'subscription_renewed': 'bg-green-100 text-green-700',
  'hazard_nearby': 'bg-orange-100 text-orange-700',
  'new_feature': 'bg-blue-100 text-blue-700',
  'voice_ready': 'bg-purple-100 text-purple-700',
  'system': 'bg-gray-100 text-gray-700',
  'broadcast': 'bg-indigo-100 text-indigo-700',
  'admin': 'bg-indigo-100 text-indigo-700'
};

export default function NotificationAnalytics({ embedded = false }: NotificationAnalyticsProps) {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [userPreferences, setUserPreferences] = useState({
    totalUsers: 0,
    notificationsEnabled: 0,
    notificationsDisabled: 0,
    referralNotificationsEnabled: 0,
    activeFcmTokens: 0,
    staleFcmTokens: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load user preferences
      const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
      let totalUsers = 0;
      let notifEnabled = 0;
      let notifDisabled = 0;
      let referralNotifEnabled = 0;
      let activeFcm = 0;
      let staleFcm = 0;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      usersSnapshot.docs.forEach(doc => {
        if (['blocked', 'invites', 'admin_logs'].includes(doc.id)) return;
        totalUsers++;
        const data = doc.data();
        if (data.notificationsEnabled !== false) notifEnabled++;
        else notifDisabled++;
        if (data.referralNotificationsEnabled !== false) referralNotifEnabled++;
        if (data.fcmToken) {
          const tokenDate = timestampToDate(data.fcmTokenUpdatedAt);
          if (tokenDate && tokenDate >= thirtyDaysAgo) activeFcm++;
          else staleFcm++;
        }
      });

      setUserPreferences({
        totalUsers,
        notificationsEnabled: notifEnabled,
        notificationsDisabled: notifDisabled,
        referralNotificationsEnabled: referralNotifEnabled,
        activeFcmTokens: activeFcm,
        staleFcmTokens: staleFcm
      });

      // Load recent notifications using collectionGroup
      const allNotifications: NotificationRecord[] = [];
      const notifSnapshot = await getDocs(
        query(
          collectionGroup(navDb, 'notifications'),
          orderBy('createdAt', 'desc'),
          limit(500)
        )
      );

      notifSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const parentPath = doc.ref.parent.parent?.id;
        if (!parentPath || ['blocked', 'invites', 'admin_logs'].includes(parentPath)) return;

        allNotifications.push({
          id: doc.id,
          userId: parentPath,
          type: data.type || 'system',
          title: data.title || '',
          body: data.body || '',
          read: data.read || false,
          createdAt: timestampToDate(data.createdAt),
          sentBy: data.sentBy
        });
      });

      setNotifications(allNotifications);
    } catch (error) {
      console.error('Error loading notification analytics:', error);
      toast.error('Failed to load notification analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Calculate analytics
  const totalNotifications = notifications.length;
  const readNotifications = notifications.filter(n => n.read).length;
  const readRate = totalNotifications > 0 ? (readNotifications / totalNotifications) * 100 : 0;
  const adminSent = notifications.filter(n => n.sentBy).length;

  // Type breakdown
  const typeCounts: Record<string, { total: number; read: number }> = {};
  notifications.forEach(n => {
    const type = n.type || 'system';
    if (!typeCounts[type]) typeCounts[type] = { total: 0, read: 0 };
    typeCounts[type].total++;
    if (n.read) typeCounts[type].read++;
  });
  const sortedTypes = Object.entries(typeCounts).sort((a, b) => b[1].total - a[1].total);

  const fcmAdoptionRate = userPreferences.totalUsers > 0
    ? ((userPreferences.activeFcmTokens + userPreferences.staleFcmTokens) / userPreferences.totalUsers * 100)
    : 0;

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Notification Analytics</h2>
            <p className="text-gray-500">Push notification performance and delivery metrics</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Total Notifications</p>
                <p className="text-2xl font-bold text-blue-700">{totalNotifications}</p>
                <p className="text-xs text-gray-500">{adminSent} admin-sent</p>
              </div>
              <Send className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Read Rate</p>
                <p className="text-2xl font-bold text-green-700">{readRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500">{readNotifications} of {totalNotifications}</p>
              </div>
              <Eye className="h-8 w-8 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Active FCM Tokens</p>
                <p className="text-2xl font-bold text-purple-700">{userPreferences.activeFcmTokens}</p>
                <p className="text-xs text-gray-500">{fcmAdoptionRate.toFixed(0)}% adoption</p>
              </div>
              <Smartphone className="h-8 w-8 text-purple-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Notifications Enabled</p>
                <p className="text-2xl font-bold text-amber-700">{userPreferences.notificationsEnabled}</p>
                <p className="text-xs text-gray-500">
                  {userPreferences.totalUsers > 0
                    ? `${(userPreferences.notificationsEnabled / userPreferences.totalUsers * 100).toFixed(0)}% of users`
                    : '0% of users'
                  }
                </p>
              </div>
              <Bell className="h-8 w-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Preferences + Type Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Notification Preferences */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              User Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Notifications Enabled</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{userPreferences.notificationsEnabled}</span>
                  <Badge className="bg-green-100 text-green-800 text-xs">
                    {userPreferences.totalUsers > 0 ? `${(userPreferences.notificationsEnabled / userPreferences.totalUsers * 100).toFixed(0)}%` : '0%'}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellOff className="h-4 w-4 text-red-500" />
                  <span className="text-sm">Notifications Disabled</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{userPreferences.notificationsDisabled}</span>
                  <Badge className="bg-red-100 text-red-800 text-xs">
                    {userPreferences.totalUsers > 0 ? `${(userPreferences.notificationsDisabled / userPreferences.totalUsers * 100).toFixed(0)}%` : '0%'}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-pink-500" />
                  <span className="text-sm">Referral Notifications</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{userPreferences.referralNotificationsEnabled}</span>
                  <Badge className="bg-pink-100 text-pink-800 text-xs">
                    {userPreferences.totalUsers > 0 ? `${(userPreferences.referralNotificationsEnabled / userPreferences.totalUsers * 100).toFixed(0)}%` : '0%'}
                  </Badge>
                </div>
              </div>

              <div className="pt-2 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Active FCM Tokens</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{userPreferences.activeFcmTokens}</span>
                  {userPreferences.staleFcmTokens > 0 && (
                    <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                      {userPreferences.staleFcmTokens} stale
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Type Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Notifications by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedTypes.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No notifications found</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {sortedTypes.map(([type, counts]) => {
                  const typeReadRate = counts.total > 0 ? (counts.read / counts.total * 100) : 0;
                  return (
                    <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Badge className={`text-xs flex-shrink-0 ${NOTIFICATION_TYPE_COLORS[type] || 'bg-gray-100 text-gray-700'}`}>
                          {NOTIFICATION_TYPE_LABELS[type] || type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm font-medium">{counts.total}</span>
                        <Badge variant="outline" className="text-xs">
                          <Eye className="h-3 w-3 mr-1" />
                          {typeReadRate.toFixed(0)}%
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-500" />
            Recent Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No notifications found</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {notifications.slice(0, 30).map(notif => (
                <div key={`${notif.userId}-${notif.id}`} className="p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {notif.read ? (
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{notif.title}</p>
                        <p className="text-xs text-gray-500 truncate">{notif.body}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <Badge className={`text-xs ${NOTIFICATION_TYPE_COLORS[notif.type] || 'bg-gray-100 text-gray-700'}`}>
                        {NOTIFICATION_TYPE_LABELS[notif.type] || notif.type}
                      </Badge>
                      <p className="text-xs text-gray-400 mt-1">
                        {notif.createdAt ? notif.createdAt.toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
